import * as z from 'zod';

export const CreateSeatingReservationRequest = z.object({
  options: z
    .object({
      reservation: z
        .object({
          _id: z
            .string()
            .describe('Reservation ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          seatingPlanId: z
            .string()
            .describe('Seating plan ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          externalSeatingPlanId: z
            .string()
            .describe(
              'External seating plan ID used for integration with third-party venue systems.'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          reservedPlaces: z
            .array(
              z.object({
                _id: z
                  .string()
                  .describe(
                    'Place ID in the format `{section_id}-{element_id}-{label}`. For example, `0-1-5` for section 0, element 1, seat 5; or `1-1-A` for the single place of an AREA element.'
                  )
                  .min(5)
                  .max(12)
                  .optional(),
                capacity: z
                  .number()
                  .int()
                  .describe(
                    'Number of people to be seated at this place. Used for area seating where multiple guests can occupy a single reservable location.\n\nDefault: `1`'
                  )
                  .min(1)
                  .max(50)
                  .optional()
                  .nullable(),
                sectionLabel: z
                  .string()
                  .describe(
                    'Section name within the venue. For example, `Orchestra` or `Balcony`.'
                  )
                  .optional()
                  .nullable(),
                areaLabel: z
                  .string()
                  .describe(
                    'Area name within a section. Used for general admission or standing areas.'
                  )
                  .optional()
                  .nullable(),
                tableLabel: z
                  .string()
                  .describe(
                    'Table identifier for table-based seating arrangements.'
                  )
                  .optional()
                  .nullable(),
                rowLabel: z
                  .string()
                  .describe('Row identifier within a section or area.')
                  .optional()
                  .nullable(),
                seatLabel: z
                  .string()
                  .describe(
                    'Individual seat identifier within a row or at a table.'
                  )
                  .optional()
                  .nullable(),
              })
            )
            .min(1)
            .max(100)
            .optional(),
          externalId: z
            .string()
            .describe(
              'External reference ID for cross-system integration. Format: `{fqdn}:{entity guid}`. For example, `wix.events.v1.ticket:12345678-1234-1234-1234-123456789012`.'
            )
            .min(1)
            .max(100)
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the reservation is updated. To prevent conflicting changes, the current revision must be passed when updating the reservation. Ignored when creating a reservation.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the seating plan was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the seating plan was updated.')
            .optional()
            .nullable(),
        })
        .describe('Reservation to create.')
        .optional(),
    })
    .optional(),
});
export const CreateSeatingReservationResponse = z.object({
  _id: z
    .string()
    .describe('Reservation ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  seatingPlanId: z
    .string()
    .describe('Seating plan ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  externalSeatingPlanId: z
    .string()
    .describe(
      'External seating plan ID used for integration with third-party venue systems.'
    )
    .min(1)
    .max(100)
    .optional()
    .nullable(),
  reservedPlaces: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'Place ID in the format `{section_id}-{element_id}-{label}`. For example, `0-1-5` for section 0, element 1, seat 5; or `1-1-A` for the single place of an AREA element.'
          )
          .min(5)
          .max(12)
          .optional(),
        capacity: z
          .number()
          .int()
          .describe(
            'Number of people to be seated at this place. Used for area seating where multiple guests can occupy a single reservable location.\n\nDefault: `1`'
          )
          .min(1)
          .max(50)
          .optional()
          .nullable(),
        sectionLabel: z
          .string()
          .describe(
            'Section name within the venue. For example, `Orchestra` or `Balcony`.'
          )
          .optional()
          .nullable(),
        areaLabel: z
          .string()
          .describe(
            'Area name within a section. Used for general admission or standing areas.'
          )
          .optional()
          .nullable(),
        tableLabel: z
          .string()
          .describe('Table identifier for table-based seating arrangements.')
          .optional()
          .nullable(),
        rowLabel: z
          .string()
          .describe('Row identifier within a section or area.')
          .optional()
          .nullable(),
        seatLabel: z
          .string()
          .describe('Individual seat identifier within a row or at a table.')
          .optional()
          .nullable(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  externalId: z
    .string()
    .describe(
      'External reference ID for cross-system integration. Format: `{fqdn}:{entity guid}`. For example, `wix.events.v1.ticket:12345678-1234-1234-1234-123456789012`.'
    )
    .min(1)
    .max(100)
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the reservation is updated. To prevent conflicting changes, the current revision must be passed when updating the reservation. Ignored when creating a reservation.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the seating plan was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the seating plan was updated.')
    .optional()
    .nullable(),
});
export const GetSeatingReservationRequest = z.object({
  reservationId: z
    .string()
    .describe('Reservation ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetSeatingReservationResponse = z.object({
  _id: z
    .string()
    .describe('Reservation ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  seatingPlanId: z
    .string()
    .describe('Seating plan ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  externalSeatingPlanId: z
    .string()
    .describe(
      'External seating plan ID used for integration with third-party venue systems.'
    )
    .min(1)
    .max(100)
    .optional()
    .nullable(),
  reservedPlaces: z
    .array(
      z.object({
        _id: z
          .string()
          .describe(
            'Place ID in the format `{section_id}-{element_id}-{label}`. For example, `0-1-5` for section 0, element 1, seat 5; or `1-1-A` for the single place of an AREA element.'
          )
          .min(5)
          .max(12)
          .optional(),
        capacity: z
          .number()
          .int()
          .describe(
            'Number of people to be seated at this place. Used for area seating where multiple guests can occupy a single reservable location.\n\nDefault: `1`'
          )
          .min(1)
          .max(50)
          .optional()
          .nullable(),
        sectionLabel: z
          .string()
          .describe(
            'Section name within the venue. For example, `Orchestra` or `Balcony`.'
          )
          .optional()
          .nullable(),
        areaLabel: z
          .string()
          .describe(
            'Area name within a section. Used for general admission or standing areas.'
          )
          .optional()
          .nullable(),
        tableLabel: z
          .string()
          .describe('Table identifier for table-based seating arrangements.')
          .optional()
          .nullable(),
        rowLabel: z
          .string()
          .describe('Row identifier within a section or area.')
          .optional()
          .nullable(),
        seatLabel: z
          .string()
          .describe('Individual seat identifier within a row or at a table.')
          .optional()
          .nullable(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  externalId: z
    .string()
    .describe(
      'External reference ID for cross-system integration. Format: `{fqdn}:{entity guid}`. For example, `wix.events.v1.ticket:12345678-1234-1234-1234-123456789012`.'
    )
    .min(1)
    .max(100)
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the reservation is updated. To prevent conflicting changes, the current revision must be passed when updating the reservation. Ignored when creating a reservation.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe('Date and time the seating plan was created.')
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe('Date and time the seating plan was updated.')
    .optional()
    .nullable(),
});
export const QuerySeatingReservationsRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .optional(),
      }),
      z.xor([
        z.object({
          cursorPaging: z.never().optional(),
          paging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Number of items to load.')
                .min(0)
                .optional()
                .nullable(),
              offset: z
                .number()
                .int()
                .describe('Number of items to skip in the current sort order.')
                .min(0)
                .optional()
                .nullable(),
            })
            .describe(
              'Paging options to limit and offset the number of items.'
            ),
        }),
        z.object({
          paging: z.never().optional(),
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('Query object with filter criteria and pagination settings.'),
});
export const QuerySeatingReservationsResponse = z.object({
  reservations: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Reservation ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        seatingPlanId: z
          .string()
          .describe('Seating plan ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        externalSeatingPlanId: z
          .string()
          .describe(
            'External seating plan ID used for integration with third-party venue systems.'
          )
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        reservedPlaces: z
          .array(
            z.object({
              _id: z
                .string()
                .describe(
                  'Place ID in the format `{section_id}-{element_id}-{label}`. For example, `0-1-5` for section 0, element 1, seat 5; or `1-1-A` for the single place of an AREA element.'
                )
                .min(5)
                .max(12)
                .optional(),
              capacity: z
                .number()
                .int()
                .describe(
                  'Number of people to be seated at this place. Used for area seating where multiple guests can occupy a single reservable location.\n\nDefault: `1`'
                )
                .min(1)
                .max(50)
                .optional()
                .nullable(),
              sectionLabel: z
                .string()
                .describe(
                  'Section name within the venue. For example, `Orchestra` or `Balcony`.'
                )
                .optional()
                .nullable(),
              areaLabel: z
                .string()
                .describe(
                  'Area name within a section. Used for general admission or standing areas.'
                )
                .optional()
                .nullable(),
              tableLabel: z
                .string()
                .describe(
                  'Table identifier for table-based seating arrangements.'
                )
                .optional()
                .nullable(),
              rowLabel: z
                .string()
                .describe('Row identifier within a section or area.')
                .optional()
                .nullable(),
              seatLabel: z
                .string()
                .describe(
                  'Individual seat identifier within a row or at a table.'
                )
                .optional()
                .nullable(),
            })
          )
          .min(1)
          .max(100)
          .optional(),
        externalId: z
          .string()
          .describe(
            'External reference ID for cross-system integration. Format: `{fqdn}:{entity guid}`. For example, `wix.events.v1.ticket:12345678-1234-1234-1234-123456789012`.'
          )
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the reservation is updated. To prevent conflicting changes, the current revision must be passed when updating the reservation. Ignored when creating a reservation.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the seating plan was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the seating plan was updated.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
  metadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used.'
        )
        .optional(),
    })
    .describe('Paging metadata for result navigation.')
    .optional(),
});
export const DeleteSeatingReservationRequest = z.object({
  _id: z
    .string()
    .describe('Reservation ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteSeatingReservationResponse = z.object({
  reservation: z
    .object({
      _id: z
        .string()
        .describe('Reservation ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      seatingPlanId: z
        .string()
        .describe('Seating plan ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      externalSeatingPlanId: z
        .string()
        .describe(
          'External seating plan ID used for integration with third-party venue systems.'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      reservedPlaces: z
        .array(
          z.object({
            _id: z
              .string()
              .describe(
                'Place ID in the format `{section_id}-{element_id}-{label}`. For example, `0-1-5` for section 0, element 1, seat 5; or `1-1-A` for the single place of an AREA element.'
              )
              .min(5)
              .max(12)
              .optional(),
            capacity: z
              .number()
              .int()
              .describe(
                'Number of people to be seated at this place. Used for area seating where multiple guests can occupy a single reservable location.\n\nDefault: `1`'
              )
              .min(1)
              .max(50)
              .optional()
              .nullable(),
            sectionLabel: z
              .string()
              .describe(
                'Section name within the venue. For example, `Orchestra` or `Balcony`.'
              )
              .optional()
              .nullable(),
            areaLabel: z
              .string()
              .describe(
                'Area name within a section. Used for general admission or standing areas.'
              )
              .optional()
              .nullable(),
            tableLabel: z
              .string()
              .describe(
                'Table identifier for table-based seating arrangements.'
              )
              .optional()
              .nullable(),
            rowLabel: z
              .string()
              .describe('Row identifier within a section or area.')
              .optional()
              .nullable(),
            seatLabel: z
              .string()
              .describe(
                'Individual seat identifier within a row or at a table.'
              )
              .optional()
              .nullable(),
          })
        )
        .min(1)
        .max(100)
        .optional(),
      externalId: z
        .string()
        .describe(
          'External reference ID for cross-system integration. Format: `{fqdn}:{entity guid}`. For example, `wix.events.v1.ticket:12345678-1234-1234-1234-123456789012`.'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the reservation is updated. To prevent conflicting changes, the current revision must be passed when updating the reservation. Ignored when creating a reservation.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe('Date and time the seating plan was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the seating plan was updated.')
        .optional()
        .nullable(),
    })
    .describe('Deleted reservation.')
    .optional(),
});
export const GetSeatingCategorySummaryByExternalIdRequest = z.object({
  externalId: z.string().describe('External seating plan ID.').min(1).max(100),
});
export const GetSeatingCategorySummaryByExternalIdResponse = z.object({
  categories: z
    .array(
      z.object({
        seatingPlanId: z
          .string()
          .describe('Seating Plan ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        externalSeatingPlanId: z
          .string()
          .describe(
            'External Seating Plan ID used for integration with third-party systems.'
          )
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        externalCategoryId: z
          .string()
          .describe(
            'External Category ID used for mapping to venue-specific category names. For example, `VIP` or `ORCHESTRA`.'
          )
          .min(1)
          .max(100)
          .optional()
          .nullable(),
        totalCapacity: z
          .number()
          .int()
          .describe('Total seating capacity available in category.')
          .optional()
          .nullable(),
        reserved: z
          .number()
          .int()
          .describe('Number of seats currently reserved in category.')
          .optional()
          .nullable(),
      })
    )
    .max(50000)
    .optional(),
});
export const GetSeatingReservationSummaryByExternalIdRequest = z.object({
  externalId: z.string().describe('External seating plan ID.').min(1).max(100),
});
export const GetSeatingReservationSummaryByExternalIdResponse = z.object({
  plan: z
    .object({
      _id: z
        .string()
        .describe('Seating plan ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      externalId: z
        .string()
        .describe(
          'Client-defined external ID for cross-referencing with external systems. Format: `{fqdn}:{entity_guid}`. For example, `wix.events.v1.event:abc-123-def`.'
        )
        .min(1)
        .max(100)
        .optional()
        .nullable(),
      title: z
        .string()
        .describe(
          'Human-friendly seating plan title. For example, `Madison Square Garden - Main Floor`.'
        )
        .min(1)
        .max(120)
        .optional()
        .nullable(),
      sections: z
        .array(
          z.object({
            _id: z
              .number()
              .int()
              .describe(
                'Section ID. Must be unique within the seating plan. Section with `id = 0` is required as the default section.'
              )
              .min(0)
              .optional(),
            title: z
              .string()
              .describe(
                'Human-readable section title. For example, `Orchestra` or `Balcony`.'
              )
              .min(1)
              .max(20)
              .optional()
              .nullable(),
            elements: z
              .array(
                z.object({
                  _id: z
                    .number()
                    .int()
                    .describe(
                      'Element ID. Must be unique within the seating plan.'
                    )
                    .min(1)
                    .optional(),
                  title: z
                    .string()
                    .describe(
                      'User-friendly title or label for the element. For example, `Row A` or `VIP Table 1`.'
                    )
                    .min(1)
                    .max(50)
                    .optional()
                    .nullable(),
                  type: z
                    .enum([
                      'AREA',
                      'ROW',
                      'MULTI_ROW',
                      'TABLE',
                      'ROUND_TABLE',
                      'SHAPE',
                    ])
                    .describe(
                      'Type of seating element, which determines capacity limits and place generation behavior. See ElementTypeEnum for available types.'
                    )
                    .optional(),
                  capacity: z
                    .number()
                    .int()
                    .describe(
                      'Number of seats or spots in this element. Not applicable for SHAPE type elements. Maximum 50,000 per element.'
                    )
                    .min(1)
                    .max(50000)
                    .optional()
                    .nullable(),
                  categoryId: z
                    .number()
                    .int()
                    .describe(
                      'ID of the category this element is assigned to for pricing and organization. References a category defined in the seating plan.'
                    )
                    .optional()
                    .nullable(),
                  sequencing: z
                    .object({
                      startAt: z
                        .string()
                        .describe(
                          'Starting value for the sequence. For example, `1` for numeric sequences or `A` for alphabetical sequences.'
                        )
                        .min(1)
                        .max(4)
                        .optional(),
                      labels: z.array(z.string()).max(2500).optional(),
                      reverseOrder: z
                        .boolean()
                        .describe(
                          'Whether to apply labels in reverse order (right-to-left instead of left-to-right). Useful for venue-specific numbering conventions.'
                        )
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Configuration for generating place labels and numbering within this element. Defines how seats are labeled (e.g., 1, 2, 3 or A, B, C).'
                    )
                    .optional(),
                  overrides: z
                    .array(
                      z.object({
                        index: z
                          .number()
                          .int()
                          .describe(
                            "Zero-based position of this place within the element's sequence. Used to identify the place's position for overrides."
                          )
                          .min(0)
                          .optional(),
                        _id: z
                          .string()
                          .describe(
                            'Unique place ID in the format `{section_id}-{element_id}-{label}`. For example, `0-1-A5` for section 0, element 1, seat A5.'
                          )
                          .optional()
                          .nullable(),
                        label: z
                          .string()
                          .describe(
                            'Human-readable label for this place, such as `A1`, `12`, or `VIP1`. Generated based on sequencing configuration or custom overrides.'
                          )
                          .min(1)
                          .max(4)
                          .optional(),
                        capacity: z
                          .number()
                          .int()
                          .describe(
                            'Maximum number of people that can occupy this place. Typically 1 for individual seats, higher for AREA type places.'
                          )
                          .optional()
                          .nullable(),
                        elementType: z
                          .enum([
                            'AREA',
                            'ROW',
                            'MULTI_ROW',
                            'TABLE',
                            'ROUND_TABLE',
                            'SHAPE',
                          ])
                          .describe(
                            "Type of the parent element that contains this place. Inherited from the element's type."
                          )
                          .optional(),
                        categoryId: z
                          .number()
                          .int()
                          .describe(
                            'ID of the category this place is assigned to. Inherited from element or row category assignment.'
                          )
                          .optional()
                          .nullable(),
                        type: z
                          .enum([
                            'STANDARD',
                            'WHEELCHAIR',
                            'ACCESSIBLE',
                            'COMPANION',
                            'OBSTRUCTED',
                            'DISCOUNT',
                          ])
                          .describe(
                            'Special characteristics or accessibility features of this place (standard, wheelchair, accessible, companion, obstructed, discount).'
                          )
                          .optional(),
                      })
                    )
                    .max(2000)
                    .optional(),
                  places: z
                    .array(
                      z.object({
                        index: z
                          .number()
                          .int()
                          .describe(
                            "Zero-based position of this place within the element's sequence. Used to identify the place's position for overrides."
                          )
                          .min(0)
                          .optional(),
                        _id: z
                          .string()
                          .describe(
                            'Unique place ID in the format `{section_id}-{element_id}-{label}`. For example, `0-1-A5` for section 0, element 1, seat A5.'
                          )
                          .optional()
                          .nullable(),
                        label: z
                          .string()
                          .describe(
                            'Human-readable label for this place, such as `A1`, `12`, or `VIP1`. Generated based on sequencing configuration or custom overrides.'
                          )
                          .min(1)
                          .max(4)
                          .optional(),
                        capacity: z
                          .number()
                          .int()
                          .describe(
                            'Maximum number of people that can occupy this place. Typically 1 for individual seats, higher for AREA type places.'
                          )
                          .optional()
                          .nullable(),
                        elementType: z
                          .enum([
                            'AREA',
                            'ROW',
                            'MULTI_ROW',
                            'TABLE',
                            'ROUND_TABLE',
                            'SHAPE',
                          ])
                          .describe(
                            "Type of the parent element that contains this place. Inherited from the element's type."
                          )
                          .optional(),
                        categoryId: z
                          .number()
                          .int()
                          .describe(
                            'ID of the category this place is assigned to. Inherited from element or row category assignment.'
                          )
                          .optional()
                          .nullable(),
                        type: z
                          .enum([
                            'STANDARD',
                            'WHEELCHAIR',
                            'ACCESSIBLE',
                            'COMPANION',
                            'OBSTRUCTED',
                            'DISCOUNT',
                          ])
                          .describe(
                            'Special characteristics or accessibility features of this place (standard, wheelchair, accessible, companion, obstructed, discount).'
                          )
                          .optional(),
                      })
                    )
                    .max(200)
                    .optional(),
                  reservationOptions: z
                    .object({
                      reserveWholeElement: z
                        .boolean()
                        .describe(
                          'Whether the entire element must be reserved as a single unit. When `true`, individual seats cannot be booked separately. Cannot be used with AREA type elements.'
                        )
                        .optional(),
                    })
                    .describe(
                      'Constraints on how this element can be reserved or booked. For example, requiring the entire element to be reserved as a unit.'
                    )
                    .optional(),
                  uiProperties: z
                    .object({
                      x: z
                        .number()
                        .describe(
                          'Horizontal position coordinate of the element in the seating chart coordinate system.'
                        )
                        .min(-1000000)
                        .max(1000000)
                        .optional()
                        .nullable(),
                      y: z
                        .number()
                        .describe(
                          'Vertical position coordinate of the element in the seating chart coordinate system.'
                        )
                        .min(-1000000)
                        .max(1000000)
                        .optional()
                        .nullable(),
                      zIndex: z
                        .number()
                        .int()
                        .describe(
                          'Layering order for overlapping elements. Higher values appear on top of lower values.'
                        )
                        .min(-1000000)
                        .max(1000000)
                        .optional()
                        .nullable(),
                      width: z
                        .number()
                        .describe(
                          'Width of the element in the coordinate system units.'
                        )
                        .min(0)
                        .max(1000000)
                        .optional()
                        .nullable(),
                      height: z
                        .number()
                        .describe(
                          'Height of the element in the coordinate system units.'
                        )
                        .min(0)
                        .max(1000000)
                        .optional()
                        .nullable(),
                      rotationAngle: z
                        .number()
                        .describe(
                          'Rotation angle of the element in degrees. Positive values rotate clockwise.'
                        )
                        .min(-180)
                        .max(180)
                        .optional()
                        .nullable(),
                      shapeType: z
                        .enum([
                          'UNKNOWN_TYPE',
                          'TEXT',
                          'RECTANGLE',
                          'ELLIPSE',
                          'LINE',
                          'ICON',
                          'IMAGE',
                        ])
                        .describe(
                          "Shape type for SHAPE elements that don't represent seating. Only applicable when element type is SHAPE."
                        )
                        .optional(),
                      fontSize: z
                        .number()
                        .int()
                        .describe('Font size for text elements in points.')
                        .min(10)
                        .max(176)
                        .optional()
                        .nullable(),
                      cornerRadius: z
                        .number()
                        .int()
                        .describe(
                          'Corner radius for rounded rectangular shapes in coordinate system units.'
                        )
                        .min(0)
                        .max(1000000)
                        .optional()
                        .nullable(),
                      seatSpacing: z
                        .number()
                        .int()
                        .describe(
                          'Horizontal spacing between individual seats within the element.'
                        )
                        .min(1)
                        .max(60)
                        .optional()
                        .nullable(),
                      hideLabel: z
                        .boolean()
                        .describe(
                          'Whether to hide labels on seats within this element. When `true`, seat labels are not displayed in the UI.'
                        )
                        .optional()
                        .nullable(),
                      labelPosition: z
                        .enum([
                          'UNKNOWN_POSITION',
                          'LEFT',
                          'RIGHT',
                          'BOTH',
                          'NONE',
                        ])
                        .describe(
                          'Position of labels relative to seats (left, right, both sides, or none).'
                        )
                        .optional(),
                      seatLayout: z.array(z.number().int()).optional(),
                      emptyTopSeatSpaces: z
                        .number()
                        .int()
                        .describe(
                          'Number of empty spaces at the top of the seating arrangement for visual spacing.'
                        )
                        .min(0)
                        .max(50)
                        .optional()
                        .nullable(),
                      text: z
                        .string()
                        .describe(
                          'Text content for TEXT type shape elements. Only applicable when shape_type is TEXT.'
                        )
                        .min(1)
                        .max(10000)
                        .optional()
                        .nullable(),
                      color: z
                        .string()
                        .describe(
                          'Primary color in hexadecimal format. For example, `#FF0000` for red.'
                        )
                        .optional()
                        .nullable(),
                      fillColor: z
                        .string()
                        .describe(
                          'Fill color for shapes in hexadecimal format. For example, `#00FF00` for green.'
                        )
                        .optional()
                        .nullable(),
                      strokeColor: z
                        .string()
                        .describe(
                          'Border color in hexadecimal format. For example, `#0000FF` for blue.'
                        )
                        .optional()
                        .nullable(),
                      strokeWidth: z
                        .number()
                        .int()
                        .describe('Border width in pixels for shape outlines.')
                        .min(1)
                        .max(6)
                        .optional()
                        .nullable(),
                      opacity: z
                        .number()
                        .int()
                        .describe(
                          'Opacity level as a percentage from 0 (transparent) to 100 (opaque).'
                        )
                        .min(0)
                        .max(100)
                        .optional()
                        .nullable(),
                      icon: z
                        .enum([
                          'UNKNOWN_ICON',
                          'ENTER',
                          'EXIT',
                          'DRINKS',
                          'WC',
                          'WC_MEN',
                          'WC_WOMEN',
                          'FOOD',
                          'STAIRS',
                          'ELEVATOR',
                          'SMOKING',
                          'CHECKROOM',
                          'STAGE',
                        ])
                        .describe(
                          'Icon type for ICON shape elements. Only applicable when shape_type is ICON.'
                        )
                        .optional(),
                      image: z
                        .object({
                          _id: z
                            .string()
                            .describe('Image ID from Wix Media Manager.')
                            .optional(),
                          height: z
                            .number()
                            .int()
                            .describe('Original image height in pixels.')
                            .optional(),
                          width: z
                            .number()
                            .int()
                            .describe('Original image width in pixels.')
                            .optional(),
                          uri: z
                            .string()
                            .describe(
                              'Deprecated. Use the `id` field instead for referencing images.'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'Image object for IMAGE shape elements. Only applicable when shape_type is IMAGE.'
                        )
                        .optional(),
                      seatNumbering: z
                        .enum([
                          'UNKNOWN_NUMBERING',
                          'NUMERIC',
                          'ODD_EVEN',
                          'ALPHABETICAL',
                        ])
                        .describe(
                          'Numbering scheme for seats within this element (numeric, alphabetical, or odd/even).'
                        )
                        .optional(),
                    })
                    .describe(
                      'Visual positioning and styling properties for rendering this element in a seating chart. Includes coordinates, dimensions, and styling options.'
                    )
                    .optional(),
                  elementGroupId: z
                    .number()
                    .int()
                    .describe(
                      'ID of the element group this element belongs to for hierarchical organization. References an ElementGroup defined in the seating plan.'
                    )
                    .optional()
                    .nullable(),
                  multiRowProperties: z
                    .object({
                      rows: z
                        .array(
                          z.object({
                            _id: z
                              .number()
                              .int()
                              .describe(
                                'Row ID. Must be unique across all multi-row elements in the seating plan.'
                              )
                              .min(1)
                              .optional(),
                            title: z
                              .string()
                              .describe(
                                'User-friendly title or label for this row. For example, `Row A` or `Front Row`.'
                              )
                              .min(1)
                              .max(50)
                              .optional()
                              .nullable(),
                            capacity: z
                              .number()
                              .int()
                              .describe(
                                'Number of seats in this row. Maximum 50,000 per row.'
                              )
                              .min(1)
                              .max(50000)
                              .optional()
                              .nullable(),
                            categoryId: z
                              .number()
                              .int()
                              .describe(
                                "Category assignment for this row, which can override the parent element's category. References a category defined in the seating plan."
                              )
                              .optional()
                              .nullable(),
                            sequencing: z
                              .object({
                                startAt: z
                                  .string()
                                  .describe(
                                    'Starting value for the sequence. For example, `1` for numeric sequences or `A` for alphabetical sequences.'
                                  )
                                  .min(1)
                                  .max(4)
                                  .optional(),
                                labels: z
                                  .array(z.string())
                                  .max(2500)
                                  .optional(),
                                reverseOrder: z
                                  .boolean()
                                  .describe(
                                    'Whether to apply labels in reverse order (right-to-left instead of left-to-right). Useful for venue-specific numbering conventions.'
                                  )
                                  .optional()
                                  .nullable(),
                              })
                              .describe(
                                'Configuration for generating seat labels within this row. Defines numbering scheme and starting position.'
                              )
                              .optional(),
                            uiProperties: z
                              .object({
                                relativeX: z
                                  .number()
                                  .describe(
                                    "Horizontal position relative to the parent multi-row element's coordinate system."
                                  )
                                  .min(-1000000)
                                  .max(1000000)
                                  .optional()
                                  .nullable(),
                                width: z
                                  .number()
                                  .describe(
                                    'Width of this row in coordinate system units.'
                                  )
                                  .min(0)
                                  .max(1000000)
                                  .optional()
                                  .nullable(),
                                seatAmount: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Number of seats in this row. Overrides the capacity field when specified.'
                                  )
                                  .min(0)
                                  .max(50)
                                  .optional()
                                  .nullable(),
                                seatSpacing: z
                                  .number()
                                  .int()
                                  .describe(
                                    'Spacing between seats in this row.'
                                  )
                                  .min(1)
                                  .max(60)
                                  .optional()
                                  .nullable(),
                                labelPosition: z
                                  .enum([
                                    'UNKNOWN_POSITION',
                                    'LEFT',
                                    'RIGHT',
                                    'BOTH',
                                    'NONE',
                                  ])
                                  .describe(
                                    'Position of labels relative to seats in this row (left, right, both sides, or none). // Position of labels relative to seats in this row.'
                                  )
                                  .optional(),
                                seatNumbering: z
                                  .enum([
                                    'UNKNOWN_NUMBERING',
                                    'NUMERIC',
                                    'ODD_EVEN',
                                    'ALPHABETICAL',
                                  ])
                                  .describe(
                                    'Numbering scheme for seats in this row (numeric, alphabetical, or odd/even).'
                                  )
                                  .optional(),
                              })
                              .describe(
                                'Visual properties specific to this row within the multi-row element. Includes positioning relative to parent element.'
                              )
                              .optional(),
                          })
                        )
                        .max(1000)
                        .optional(),
                      verticalSequencing: z
                        .object({
                          startAt: z
                            .string()
                            .describe(
                              'Starting value for row labeling. For example, `A` for alphabetical sequences or `1` for numeric sequences.'
                            )
                            .min(1)
                            .max(4)
                            .optional(),
                          rowNumbering: z
                            .enum([
                              'UNKNOWN_NUMBERING',
                              'NUMERIC',
                              'ODD_EVEN',
                              'ALPHABETICAL',
                            ])
                            .describe(
                              'Numbering scheme for row labels (numeric, alphabetical, or odd/even).'
                            )
                            .optional(),
                          reverseOrder: z
                            .boolean()
                            .describe(
                              'Whether to label rows in reverse order (bottom-to-top instead of top-to-bottom).'
                            )
                            .optional()
                            .nullable(),
                        })
                        .describe(
                          'Configuration for labeling rows vertically (e.g., Row A, Row B, Row C). Defines how row labels are generated.'
                        )
                        .optional(),
                      rowSpacing: z
                        .number()
                        .int()
                        .describe(
                          'Vertical spacing between rows in the multi-row element. Measured in coordinate system units.'
                        )
                        .min(1)
                        .max(60)
                        .optional()
                        .nullable(),
                      seatAmount: z
                        .number()
                        .int()
                        .describe(
                          'Number of seats per row in the multi-row element.'
                        )
                        .min(0)
                        .max(50)
                        .optional()
                        .nullable(),
                    })
                    .describe(
                      'Additional properties for MULTI_ROW type elements, defining individual rows and their configurations. Only applicable when type is MULTI_ROW.'
                    )
                    .optional(),
                })
              )
              .max(1000)
              .optional(),
            totalCapacity: z
              .number()
              .int()
              .describe(
                'Total capacity of all elements within this section. Automatically calculated by summing element capacities.'
              )
              .optional()
              .nullable(),
            default: z
              .boolean()
              .describe(
                'Whether this is the default section. Always `true` for section with `id = 0`.'
              )
              .optional(),
          })
        )
        .max(100)
        .optional(),
      categories: z
        .array(
          z.object({
            _id: z
              .number()
              .int()
              .describe('Category ID. Must be unique within the seating plan.')
              .min(1)
              .optional(),
            externalId: z
              .string()
              .describe(
                'Client-defined external ID for cross-referencing with pricing or ticketing systems. Format: `{entity_fqdn}:{entity_id}`.'
              )
              .min(1)
              .max(100)
              .optional()
              .nullable(),
            title: z
              .string()
              .describe(
                'Human-readable category name, such as `VIP`, `General Admission`, or `Student Discount`.'
              )
              .min(1)
              .max(120)
              .optional(),
            totalCapacity: z
              .number()
              .int()
              .describe(
                'Total capacity of all places assigned to this category. Automatically calculated by summing place capacities.'
              )
              .optional()
              .nullable(),
            places: z
              .array(
                z.object({
                  index: z
                    .number()
                    .int()
                    .describe(
                      "Zero-based position of this place within the element's sequence. Used to identify the place's position for overrides."
                    )
                    .min(0)
                    .optional(),
                  _id: z
                    .string()
                    .describe(
                      'Unique place ID in the format `{section_id}-{element_id}-{label}`. For example, `0-1-A5` for section 0, element 1, seat A5.'
                    )
                    .optional()
                    .nullable(),
                  label: z
                    .string()
                    .describe(
                      'Human-readable label for this place, such as `A1`, `12`, or `VIP1`. Generated based on sequencing configuration or custom overrides.'
                    )
                    .min(1)
                    .max(4)
                    .optional(),
                  capacity: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of people that can occupy this place. Typically 1 for individual seats, higher for AREA type places.'
                    )
                    .optional()
                    .nullable(),
                  elementType: z
                    .enum([
                      'AREA',
                      'ROW',
                      'MULTI_ROW',
                      'TABLE',
                      'ROUND_TABLE',
                      'SHAPE',
                    ])
                    .describe(
                      "Type of the parent element that contains this place. Inherited from the element's type."
                    )
                    .optional(),
                  categoryId: z
                    .number()
                    .int()
                    .describe(
                      'ID of the category this place is assigned to. Inherited from element or row category assignment.'
                    )
                    .optional()
                    .nullable(),
                  type: z
                    .enum([
                      'STANDARD',
                      'WHEELCHAIR',
                      'ACCESSIBLE',
                      'COMPANION',
                      'OBSTRUCTED',
                      'DISCOUNT',
                    ])
                    .describe(
                      'Special characteristics or accessibility features of this place (standard, wheelchair, accessible, companion, obstructed, discount).'
                    )
                    .optional(),
                })
              )
              .max(50000)
              .optional(),
          })
        )
        .max(100)
        .optional(),
      _createdDate: z
        .date()
        .describe('Date and time the seating plan was created.')
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe('Date and time the seating plan was updated.')
        .optional()
        .nullable(),
      totalCapacity: z
        .number()
        .int()
        .describe(
          'Total number of seats across all sections and elements in the seating plan. Automatically calculated by summing element capacities.'
        )
        .optional()
        .nullable(),
      totalCategories: z
        .number()
        .int()
        .describe(
          'Total number of categories defined in the seating plan. Automatically calculated.'
        )
        .optional()
        .nullable(),
      uncategorizedPlaces: z
        .array(
          z.object({
            index: z
              .number()
              .int()
              .describe(
                "Zero-based position of this place within the element's sequence. Used to identify the place's position for overrides."
              )
              .min(0)
              .optional(),
            _id: z
              .string()
              .describe(
                'Unique place ID in the format `{section_id}-{element_id}-{label}`. For example, `0-1-A5` for section 0, element 1, seat A5.'
              )
              .optional()
              .nullable(),
            label: z
              .string()
              .describe(
                'Human-readable label for this place, such as `A1`, `12`, or `VIP1`. Generated based on sequencing configuration or custom overrides.'
              )
              .min(1)
              .max(4)
              .optional(),
            capacity: z
              .number()
              .int()
              .describe(
                'Maximum number of people that can occupy this place. Typically 1 for individual seats, higher for AREA type places.'
              )
              .optional()
              .nullable(),
            elementType: z
              .enum([
                'AREA',
                'ROW',
                'MULTI_ROW',
                'TABLE',
                'ROUND_TABLE',
                'SHAPE',
              ])
              .describe(
                "Type of the parent element that contains this place. Inherited from the element's type."
              )
              .optional(),
            categoryId: z
              .number()
              .int()
              .describe(
                'ID of the category this place is assigned to. Inherited from element or row category assignment.'
              )
              .optional()
              .nullable(),
            type: z
              .enum([
                'STANDARD',
                'WHEELCHAIR',
                'ACCESSIBLE',
                'COMPANION',
                'OBSTRUCTED',
                'DISCOUNT',
              ])
              .describe(
                'Special characteristics or accessibility features of this place (standard, wheelchair, accessible, companion, obstructed, discount).'
              )
              .optional(),
          })
        )
        .max(50000)
        .optional(),
      version: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Version number of the seating plan, incremented by 1 each time the plan is updated. Used for version management and tracking changes.'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe(
          'Additional custom fields for extending the seating plan with app-specific data. Learn more about @extended fields.'
        )
        .optional(),
      uiProperties: z
        .object({
          backgroundColor: z
            .string()
            .describe(
              'Background color in hexadecimal format. For example, `#F0F0F0` for light gray.'
            )
            .optional()
            .nullable(),
          backgroundOpacity: z
            .number()
            .int()
            .describe(
              'Background opacity as a percentage from 0 (transparent) to 100 (opaque).'
            )
            .min(0)
            .max(100)
            .optional()
            .nullable(),
        })
        .describe(
          'Visual settings for the seating plan, such as background color and opacity. Used by seating chart interfaces for rendering.'
        )
        .optional(),
      elementGroups: z
        .array(
          z.object({
            _id: z
              .number()
              .int()
              .describe(
                'Element group ID. Must be unique within the seating plan.'
              )
              .min(1)
              .optional(),
            parentElementGroupId: z
              .number()
              .int()
              .describe(
                'ID of the parent group for creating nested group hierarchies. References another ElementGroup in the same seating plan.'
              )
              .optional()
              .nullable(),
            uiProperties: z
              .object({
                x: z
                  .number()
                  .describe(
                    'Horizontal position coordinate of the group container.'
                  )
                  .min(-1000000)
                  .max(1000000)
                  .optional()
                  .nullable(),
                y: z
                  .number()
                  .describe(
                    'Vertical position coordinate of the group container.'
                  )
                  .min(-1000000)
                  .max(1000000)
                  .optional()
                  .nullable(),
                width: z
                  .number()
                  .describe(
                    'Width of the group bounding box in coordinate system units.'
                  )
                  .min(0)
                  .max(1000000)
                  .optional()
                  .nullable(),
                height: z
                  .number()
                  .describe(
                    'Height of the group bounding box in coordinate system units.'
                  )
                  .min(0)
                  .max(1000000)
                  .optional()
                  .nullable(),
                rotationAngle: z
                  .number()
                  .describe(
                    'Rotation angle of the group in degrees. Applied to all elements within the group.'
                  )
                  .min(-180)
                  .max(180)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Visual properties for rendering this group in the UI. Includes positioning and dimensions for the group container.'
              )
              .optional(),
          })
        )
        .max(1000)
        .optional(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          "Optimistic concurrency control; preserved from request, defaults to current plan's revision when absent."
        )
        .optional()
        .nullable(),
    })
    .describe('Complete seating plan structure.')
    .optional(),
  places: z
    .array(
      z.object({
        placeId: z.string().describe('Place ID.').optional(),
        occupied: z
          .number()
          .int()
          .describe('Number of occupied seats or capacity units at this place.')
          .optional(),
      })
    )
    .max(50000)
    .optional(),
});
